import * as React from 'react';
import { DataSignal } from 's-js';
import SuperCritical, { SuperCriticalComponent } from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';
import { NullableMeasurement } from 'data/models/Measurement';
import { LloydServiceVessel } from 'data/models/LloydServiceVessel';
import { MarineTrafficServiceVessel } from 'data/models/MarineTrafficServiceVessel';
import { PSixServiceVessel } from 'data/models/PSixServiceVessel';
import { Vessel } from 'data/models/Vessel';
import { VesselStatuses, VesselStatusDisplayName } from 'server/enums/VesselStatus';
import { VesselServiceOverride, VesselViewModel, OverrideServiceVesselViewModel } from './model';
import { NullableNumericInput, NullableIntegerInput } from 'portcall/numericinput/view';
import * as cx from 'classnames';

export const VesselServiceOverrideView = SuperCritical(({ model: m } : { model : VesselServiceOverride }) =>
        <PageBody model={m} />
);

let PageBody = SuperCritical(({ model: m } : { model : VesselServiceOverride }) =>
    <div className="page-body">
        <div className="pc-card main-page">
            <div className="pc-card-header">
                <div className="pc-card-title-vessel">
                    <span className="pc-card-title">{m.Vessel() && m.Vessel()!.Name()}</span>
                </div>
                {m.Model.CanEdit() && <div className="pc-card-manage-sources"><a href="" onClick={noDefault(m.EditSources)}>Edit Sources</a></div> }
            </div>
            <div className="profile">
                <table className="table">
                    <TableHead />
                    <TableBody model={m} />
                </table>
            </div>
        </div>
    </div>
);

let TableHead = SuperCritical(() =>
    <thead>
        <tr>
            <th className="table-header-name"></th>
            <th className="table-header-column">Vessel</th>
            <th className="table-header-column">Override</th>
            <th className="table-header-column">Lloyd</th>
            <th className="table-header-column">Marine Traffic</th>
            <th className="table-header-column">PSix</th>
        </tr>
    </thead>
);

let TableBody = SuperCritical(({ model: m } : { model : VesselServiceOverride }) =>
    <tbody>
        <StringOverrideRow model={m} label="Name" inputPlaceholder="Vessel Name"
            vesselProp={v => v.Name}
            overrideProp={o => o.Name}
            serviceVesselProp={sv => sv.Name} />
        <StringOverrideRow model={m} label="IMO"
            vesselProp={v => v.IMO}
            overrideProp={o => o.IMO}
            serviceVesselProp={sv => sv.IMO} />
        <StringOverrideRow model={m} label="MMSID"
            vesselProp={v => v.MMSID}
            overrideProp={o => o.Model.MMSID}
            serviceVesselProp={sv => sv.MMSID} />
        <StringOverrideRow model={m} label="VIN"
            vesselProp={v => v.VIN}
            overrideProp={o => o.Model.VIN}
            serviceVesselProp={sv => sv.VIN} />
        <StringOverrideRow model={m} label="Call Sign"
            vesselProp={v => v.CallSign}
            overrideProp={o => o.Model.CallSign}
            serviceVesselProp={sv => sv.CallSign} />
        <MeasurementOverrideRow model={m} label="Beam"
            vesselProp={v => v.Beam}
            overrideProp={o => o.Model.Beam}
            serviceVesselProp={sv => sv.Beam} />
        <MeasurementOverrideRow model={m} label="Length"
            vesselProp={v => v.Length}
            overrideProp={o => o.Model.Length}
            serviceVesselProp={sv => sv.Length} />
        <UnitlessMeasurementOverrideRow model={m} label="Gross Tonnage"
            vesselProp={v => v.GrossTonnage}
            overrideProp={o => o.Model.GrossTonnage}
            serviceVesselProp={sv => sv.GrossTonnage} />
        <MeasurementOverrideRow model={m} label="Max Draft"
            vesselProp={v => v.MaxDraft}
            overrideProp={o => o.Model.MaxDraft}
            serviceVesselProp={sv => sv.MaxDraft} />
        <StringOverrideRow model={m} label="Vessel Type"
            vesselProp={v => v.VesselType}
            overrideProp={o => o.VesselType}
            // Even though we collect VesselType for PSixServiceVessels, the value is apparently so unreliable
            // that it's not worth displaying in the UI. Our server-side precedence service also ignores it.
            serviceVesselProp={sv => sv instanceof PSixServiceVessel ? (() => '') : sv.VesselType}
        />
        <BooleanOverrideRow model={m} label="Cruise Vessel"
            vesselProp={v => v.IsCruiseVessel}
            overrideProp={o => o.Model.IsCruiseVessel}
            serviceVesselProp={sv => sv.IsCruiseVessel} />
        <IntegerOverrideRow model={m} label="Guests"
            vesselProp={v => v.ExpectedPassengerCount}
            overrideProp={o => o.Model.ExpectedPassengerCount}
            serviceVesselProp={sv => sv.ExpectedPassengerCount} />
        <BooleanOverrideRow model={m} label="Government Vessel"
            vesselProp={v => v.IsGovernmentVessel}
            overrideProp={o => o.Model.IsGovernmentVessel}
            serviceVesselProp={sv => sv.IsGovernmentVessel} />
        <VesselFlagRow model={m} />
        <VesselStatusRow model={m} />
    </tbody>
);

let VesselFlagRow = SuperCritical(({ model: m } : { model : VesselServiceOverride }) => {
    let overrideFlagId = m.Vessel()
        && m.Vessel()!.OverrideServiceVessel()
        && m.Vessel()!.OverrideServiceVessel()!.Model.FlagId,
        serviceVesselProp : ServiceVesselProp<() => string | null> = v => () => v.Flag() && v.Flag()!.Name();
    return (
        <tr>
            <td className="vessel-particular-label">Flag</td>
            <td className="vessel-particular-detail">
                {m.Vessel() && m.Vessel()!.Flag() && m.Vessel()!.Flag()!.Name()}
            </td>
            <td className="vessel-particular-override flag">
                {m.Vessel() && m.Vessel()!.OverrideServiceVessel() && (m.Model.CanEdit() ?
                    <span>
                        <select className="pc-select-secondary country_dropdown"
                            value={overrideFlagId!() === null ? '' : '' + overrideFlagId!()}
                            onChange={e => overrideFlagId!(e.currentTarget.value === '' ? null : +e.currentTarget.value)}>
                            <option value="">---</option>
                            {m.Countries().map(c =>
                                <option key={c.Id()} value={c.Id()}>{c.Name()}</option>
                            )}
                        </select>
                        <span className="pc-select-chevron"></span>
                    </span>
                : m.Vessel()!.OverrideServiceVessel()!.Model.Flag()
                        && m.Vessel()!.OverrideServiceVessel()!.Model.Flag()!.Name()
                )}
            </td>
            <td className="vessel-particular-detail">{m.Vessel() && m.Vessel()!.Model.LloydServiceVessel() && m.Vessel()!.Model.LloydServiceVessel()!.Flag() && m.Vessel()!.Model.LloydServiceVessel()!.Flag()!.Name()}</td>
            <StringServiceVesselCell model={m} serviceVesselProp={serviceVesselProp} serviceVesselRef={v => v.MarineTrafficServiceVessel()} />
            <StringServiceVesselCell model={m} serviceVesselProp={serviceVesselProp} serviceVesselRef={v => v.PSixServiceVessel()} />
        </tr>
    );
});

let VesselStatusRow = SuperCritical(({ model: m } : { model : VesselServiceOverride }) =>
    <tr>
        <td className="vessel-particular-label">Vessel Status</td>
        <td className="vessel-particular-detail status">
            {m.Vessel() && (m.Model.CanEdit() ?
                <span>
                    <select className="pc-select-secondary status_dropdown"
                        value={m.Vessel()!.Model.Status()}
                        onChange={e => m.Vessel()!.Model.Status(+e.currentTarget.value)}>
                        {VesselStatuses.map(({ Id, Name }) =>
                            <option key={Id} value={Id}>{Name}</option>
                        )}
                    </select>
                    <span className="pc-select-chevron"></span>
                </span>
                : VesselStatusDisplayName[m.Vessel()!.Model.Status()]
            )}
        </td>
        <td className="vessel-particular-override"></td>
        <td className="vessel-particular-detail"></td>
        <td className="vessel-particular-detail"></td>
        <td className="vessel-particular-detail"></td>
    </tr>
);

type VesselProp<TResult> = (v : VesselViewModel) => TResult;
type OverrideProp<TResult> = (v : OverrideServiceVesselViewModel) => TResult;
type ServiceVesselProp<TResult> = (v : LloydServiceVessel | MarineTrafficServiceVessel | PSixServiceVessel) => TResult;
type ServiceVesselRef = (v : Vessel) => LloydServiceVessel | MarineTrafficServiceVessel | PSixServiceVessel | null;

interface OverrideRowProps<TVesselResult, TOverrideResult, TServiceVesselResult> {
    model : VesselServiceOverride;
    label : string;
    inputPlaceholder? : string;
    vesselProp : VesselProp<TVesselResult>;
    overrideProp : OverrideProp<TOverrideResult>;
    serviceVesselProp : ServiceVesselProp<TServiceVesselResult>;
}

abstract class OverrideRow<TVesselResult, TOverrideResult, TServiceVesselResult> extends SuperCriticalComponent<OverrideRowProps<TVesselResult, TOverrideResult, TServiceVesselResult>> {
    abstract vesselDisplay(val : TVesselResult) : React.ReactElement<any> | string | null;
    abstract overrideDisplay(val : TOverrideResult, m : VesselServiceOverride) : React.ReactElement<any> | string | null;
    abstract overrideInputElement(val : TOverrideResult, placeholder : string, m : VesselServiceOverride) : React.ReactElement<any> | null;
    abstract serviceVesselCell(m : VesselServiceOverride, serviceVesselProp : ServiceVesselProp<TServiceVesselResult>, serviceVesselRef : ServiceVesselRef) : React.ReactElement<any>;

    srender({ model: m, label, inputPlaceholder, vesselProp, overrideProp, serviceVesselProp } : OverrideRowProps<TVesselResult, TOverrideResult, TServiceVesselResult>) {
        return (
            <tr>
                <td className="vessel-particular-label">{label}</td>
                <td className="vessel-particular-detail">{m.Vessel() && this.vesselDisplay(vesselProp(m.Vessel()!))}</td>
                <td className={cx("vessel-particular-override", {
                    'no-override': !m.Model.CanEdit()
                })}>
                    {m.Vessel() && m.Vessel()!.OverrideServiceVessel() && (m.Model.CanEdit() ?
                        this.overrideInputElement(overrideProp(m.Vessel()!.OverrideServiceVessel()!), inputPlaceholder || label, m)
                      : this.overrideDisplay(overrideProp(m.Vessel()!.OverrideServiceVessel()!), m)
                    )}
                </td>
                {this.serviceVesselCell(m, serviceVesselProp, v => v.LloydServiceVessel())}
                {this.serviceVesselCell(m, serviceVesselProp, v => v.MarineTrafficServiceVessel())}
                {this.serviceVesselCell(m, serviceVesselProp, v => v.PSixServiceVessel())}
            </tr>
        );
    }
}

class StringOverrideRow extends OverrideRow<() => string | null, (val? : string) => string | null, () => string | null> {
    vesselDisplay(val : () => string | null) {
        return val() || '';
    }
    
    overrideDisplay(val : (_val? : string) => string | null) {
        return val() || '';
    }

    overrideInputElement(val : (_val? : string) => string | null, placeholder : string) {
        return (
            <input placeholder={placeholder}
                value={val() || ''}
                onChange={e => val(e.currentTarget.value)} />
        );
    }

    serviceVesselCell(m : VesselServiceOverride, serviceVesselProp : ServiceVesselProp<() => string | null>, serviceVesselRef : ServiceVesselRef) {
        return (
            <StringServiceVesselCell model={m} serviceVesselProp={serviceVesselProp} serviceVesselRef={serviceVesselRef} />
        );
    }
}

class NumericOverrideRow extends OverrideRow<() => number | null, DataSignal<number | null>, () => number | null> {
    vesselDisplay(val : () => number | null) {
        return NumberOrBlank(val());
    }

    overrideDisplay(val : () => number | null) {
        return NumberOrBlank(val());
    }

    overrideInputElement(val : DataSignal<number | null>, placeholder : string) {
        return (
            <NullableNumericInput placeholder={placeholder} value={val} />
        );
    }

    serviceVesselCell(m : VesselServiceOverride, serviceVesselProp : ServiceVesselProp<() => number | null>, serviceVesselRef : ServiceVesselRef) {
        return (
            <NumericServiceVesselCell model={m} serviceVesselProp={serviceVesselProp} serviceVesselRef={serviceVesselRef} />
        );
    }
}

class IntegerOverrideRow extends NumericOverrideRow {
    overrideInputElement(val : DataSignal<number | null>, placeholder : string) {
        return (
            <NullableIntegerInput placeholder={placeholder} value={val} />
        );
    }
}

class MeasurementOverrideRow extends OverrideRow<() => NullableMeasurement, NullableMeasurement, NullableMeasurement> {
    constructor(props : OverrideRowProps<() => NullableMeasurement, NullableMeasurement, NullableMeasurement>);
    constructor(props : OverrideRowProps<() => NullableMeasurement, NullableMeasurement, NullableMeasurement>, showUnits : boolean);
    constructor(props : OverrideRowProps<() => NullableMeasurement, NullableMeasurement, NullableMeasurement>, protected readonly showUnits : boolean = true) {
        super(props);
    }

    vesselDisplay(val : () => NullableMeasurement) {
        return val().toString({ units: this.showUnits });
    }

    overrideDisplay(val : NullableMeasurement) {
        return val.toString({ units: this.showUnits });
    }

    overrideInputElement(val : NullableMeasurement, placeholder : string) {
        return (
            <span>
                <NullableNumericInput placeholder={placeholder}
                    value={val.Num} />
                {' '}
                {this.showUnits && <span>{val.UOM()}</span>}
            </span>
        );
    }

    serviceVesselCell(m : VesselServiceOverride, serviceVesselProp : ServiceVesselProp<NullableMeasurement>, serviceVesselRef : ServiceVesselRef) {
        return this.showUnits ?
            <MeasurementServiceVesselCell         model={m} serviceVesselProp={serviceVesselProp} serviceVesselRef={serviceVesselRef} />
          : <UnitlessMeasurementServiceVesselCell model={m} serviceVesselProp={serviceVesselProp} serviceVesselRef={serviceVesselRef} />;
    }
}

class UnitlessMeasurementOverrideRow extends MeasurementOverrideRow {
    constructor(props : OverrideRowProps<() => NullableMeasurement, NullableMeasurement, NullableMeasurement>) {
        super(props, false);
    }
}

class BooleanOverrideRow extends OverrideRow<() => boolean | null, DataSignal<boolean | null>, () => boolean | null> {
    vesselDisplay(val : () => boolean | null) {
        return (
            <input type="checkbox" checked={!!val()} disabled={true} />
        );
    }

    overrideDisplay(val : DataSignal<boolean | null>) {
        return YesNoUnknown(val());
    }

    overrideInputElement(val : DataSignal<boolean | null>, placeholder : string) {
        return (
            // TH 11/11/2016 -- We used to use an <input type="checkbox"> here
            // Switching to a <select> to distinguish null from false.
            <span>
                <select className="pc-select-secondary" value={val() ? 1 : val() === false ? -1 : 0}
                    onChange={e => val(e.currentTarget.value === '1' ? true : e.currentTarget.value === '-1' ? false : null)}>
                    <option value={0}>--</option>
                    <option value={1}>Yes</option>
                    <option value={-1}>No</option>
                </select>
                <span className="pc-select-chevron"></span>
            </span>
        );
    }

    serviceVesselCell(m : VesselServiceOverride, serviceVesselProp : ServiceVesselProp<() => boolean | null>, serviceVesselRef : ServiceVesselRef) {
        return (
            <BooleanServiceVesselCell model={m} serviceVesselProp={serviceVesselProp} serviceVesselRef={serviceVesselRef} />
        );
    }
}

interface ServiceVesselCellProps<TServiceVesselResult> {
    model : VesselServiceOverride;
    serviceVesselProp : ServiceVesselProp<TServiceVesselResult>;
    serviceVesselRef : ServiceVesselRef;
}

abstract class ServiceVesselCell<TServiceVesselResult> extends SuperCriticalComponent<ServiceVesselCellProps<TServiceVesselResult>> {
    abstract display(val : TServiceVesselResult) : React.ReactElement<any> | string;

    srender({ model: m, serviceVesselProp, serviceVesselRef } : ServiceVesselCellProps<TServiceVesselResult>) {
        const serviceVessel = m.Vessel() && serviceVesselRef(m.Vessel()!.Model);
        return (
            <td className="vessel-particular-detail">{serviceVessel && this.display(serviceVesselProp(serviceVessel))}</td>
        );
    }
}

class StringServiceVesselCell extends ServiceVesselCell<() => string | null> {
    display(val : () => string | null) {
        return val() || '';
    }
}

class NumericServiceVesselCell extends ServiceVesselCell<() => number | null> {
    display(val : () => number | null) {
        return NumberOrBlank(val());
    }
}

class MeasurementServiceVesselCell extends ServiceVesselCell<NullableMeasurement> {
    constructor(props : ServiceVesselCellProps<NullableMeasurement>);
    constructor(props : ServiceVesselCellProps<NullableMeasurement>, showUnits : boolean);
    constructor(props : ServiceVesselCellProps<NullableMeasurement>, protected readonly showUnits : boolean = true) {
        super(props);
    }

    display(val : NullableMeasurement) {
        return val.toString({ units: this.showUnits });
    }
}

class UnitlessMeasurementServiceVesselCell extends MeasurementServiceVesselCell {
    constructor(props : ServiceVesselCellProps<NullableMeasurement>) {
        super(props, false);
    }
}

class BooleanServiceVesselCell extends ServiceVesselCell<() => boolean | null> {
    display(val : () => boolean | null) {
        // TH: 11/11/2016 -- We used to use an <input type="checkbox"> here.
        // Switching to Yes/No/empty to distinguish null (as in unknown) from false (as in known to be false).
        return YesNoUnknown(val());
    }
}

function NumberOrBlank(val : number | null) {
    return val === null ? '' : val.toLocaleString();
}

function YesNoUnknown(val : boolean | null) {
    return val ? 'Yes' : val === false ? 'No' : '';
}



// WEBPACK FOOTER //
// ./apps/portcall/vesselserviceoverride/view.tsx